import { format } from "date-fns"
import React from "react"
import { type DateInterval } from "react-day-picker"
import { useTranslation } from "react-i18next"
import DateIcon from "@/assets/icons/task/date-icon.tsx"
import DrawerContentWrapper from "@/components/drawer-content-wrapper/drawer-content-wrapper.tsx"
import GenericTypeDrawerHeader from "@/components/generic-type-drawer/generic-type-drawer-header/generic-type-drawer-header.tsx"
import { Button } from "@/components/shadcn/button.tsx"
import Calendar from "@/components/shadcn/calendar/calendar.tsx"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/shadcn/drawer.tsx"
import RequiredIndicator from "@/features/farm/components/required-indicator/required-indicator.tsx"
import { cn } from "@/utils/styles.ts"

interface Props {
  placeholder?: string
  value: string
  onChange: (date: string) => void
  className?: string
  dateInterval?: DateInterval
  icon?: React.ReactNode
  isRequired?: boolean
}

export const DrawerDatePicker: React.FC<Props> = ({
  placeholder = "Data",
  value,
  onChange,
  className,
  dateInterval,
  icon = <DateIcon />,
  isRequired = false,
}) => {
  const { t } = useTranslation()
  const receivedDate = value ? new Date(value) : undefined
  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onChange(format(new Date(date), "yyyy-MM-dd"))
    } else {
      onChange("")
    }
  }

  return (
    <Drawer>
      <DrawerTrigger>
        <div className={cn("flex w-full items-center gap-[10px]", className)}>
          {icon}
          <div className={"flex flex-col items-start"}>
            <p>{t(placeholder)}</p>
            {value && (
              <p className={"text-brown-500"}>{format(value, "dd.MM.yyyy")}</p>
            )}
          </div>
          {isRequired && <RequiredIndicator />}
        </div>
      </DrawerTrigger>
      <DrawerContent className={"h-3/4"}>
        <GenericTypeDrawerHeader title={placeholder} />
        <DrawerContentWrapper className={"flex flex-col justify-between p-3"}>
          <Calendar
            mode={"single"}
            selected={receivedDate}
            defaultMonth={receivedDate}
            onSelect={handleSelect}
            style={{
              margin: "0px",
            }}
            disabled={
              dateInterval
                ? {
                    after: dateInterval.before,
                    before: dateInterval.after,
                  }
                : undefined
            }
          />
          <DrawerClose className={"w-full"}>
            <Button size={"lg"} className={"w-full text-white"}>
              Ok
            </Button>
          </DrawerClose>
        </DrawerContentWrapper>
      </DrawerContent>
    </Drawer>
  )
}
